/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {CreateJob} from '@ion/core/src/types/entities';
import {createListenerRegistry} from '@ion/core/src/utils/ListenerRegistry';

export type JobRerunListener = (job: CreateJob) => void;

const listeners = createListenerRegistry<[CreateJob]>();

/**
 * Registers a listener invoked with each job a rerun creates, and returns its
 * disposer.
 *
 * This exists because of the import direction. `useJobSearchRerun` is the one
 * place all three rerun surfaces go through - the Jobs 2.0 grid, the job
 * details dialog, and the notification panel's own Rerun - but the things an
 * application wants to do with the new job live in the application, and this
 * package cannot import from one. So the event is published here and the
 * application subscribes at start-up.
 *
 * Deliberately separate from the Create Job dialog's `JOB_CREATE_RESPONSE`.
 * That path carries a per-job answer the user gave in the dialog; a rerun has
 * no dialog and no answer, so a listener has to fall back to whatever default
 * the application stores. Merging the two would lose the dialog's override.
 *
 * ```typescript
 *
 *   import {onJobRerun} from '@ion/applications/src/components/jobs/JobRerunListeners';
 *
 *   ...
 *   const dispose = onJobRerun(function(job) {
 *     watch(job.id); // ... the application decides what a rerun means ...
 *   });
 *
 *   // ... and at sign-out ...
 *   dispose();
 *
 * ```
 *
 * @param   listener Invoked with the created job once the service answers.
 * @returns The function which removes this listener, and only this one.
 */
export const onJobRerun = (listener: JobRerunListener): (() => void) => (
  listeners.subscribe(listener)
);

/**
 * Publishes one rerun to every listener. Called from the rerun hook's success
 * handler, so `job` is the service's answer rather than the clone sent to it -
 * the new job's own id is the whole point.
 */
export const notifyJobRerun = (job: CreateJob): void => (
  listeners.notify(job)
);

/**
 * Clears the module. Module state outlives a test case, so this exists for test
 * isolation - never call it from application code.
 */
export const reset = (): void => (
  listeners.clear()
);
